// @flow
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { connect } from 'react-redux';
import { OutboundLink } from 'gatsby-plugin-google-gtag';
import cx from 'classnames';
import { navigate, Link } from 'gatsby';
import { isAndroid } from 'react-device-detect';
import debounce from 'lodash.debounce';
import ReactGA from 'react-ga';

import { updatePairs as updateFormPairs } from '../redux/basic-business';
import { updatePairs as updateUIPairs } from '../redux/ui';
import { updatePairs as updateUITBCPairs } from '../redux/ui-to-be-cached';
import { updateChannel } from '../redux/channel';
import { flattenObject, isEmailVerifyResultValid, scrollToFirstError } from '../utils';

/* components */
import Layout from '../components/Layout/Layout';
import InputBox from '../components/Forms/InputBox';
import withValidation from '../components/Forms/withValidation';
import validators from '../components/Forms/validators';
import {
  NOTIFICATION_DEBOUNCE,
  NOTIFICATION_TIMEOUT,
  EMAIL_VERIFICATION_URL,
  SAVE_APPLICATION_URL,
  TERMS_PERSONAL_INFO_CONSENT_URL,
  CUSTOMER_SERVICE_INQUIRY_FORM_URL,
} from '../constants';
import { getChannelFromParam } from '../utils/getChannelFromParam';
import { getRefCodeFromParam } from '../utils/getRefCodeFromParam';
import { updateRefCode } from '../redux/refCode';

const formValidators = {
  title: validators.empty,
  titleKana: validators.katakana,
  contactName: validators.specialCharacter,
  contactPhone: validators.contactPhone,
  contactEmail: validators.email,
};

const BasicBusinessPage = ({
  onValidateAll,
  onValidate,
  errors,

  title,
  titleKana,

  contactPhone,
  contactName,
  contactEmail,

  updatePairs,
  _updateUIPairs,
  _updateUITBCPairs,
  _updateChannel,
  _updateRefCode,

  isNotificationVisible,
  isFromConfirm,
}) => {
  useEffect(() => {
    _updateUITBCPairs({ merchantType: 'business' });
    const channel = getChannelFromParam();

    if (channel) {
      _updateChannel({ channel });
    }

    const refCode = getRefCodeFromParam();

    if (refCode) {
      _updateRefCode({ refCode });
    }
  }, []);

  const [emailVerificationError, setEmailVerificationError] = useState('');

  const onChange = (event) => {
    const { name } = event.currentTarget;
    const { value } = event.currentTarget;

    const showSavedNotification = debounce(() => {
      if (!isNotificationVisible) {
        _updateUIPairs({ isNotificationVisible: true });

        setTimeout(() => {
          _updateUIPairs({ isNotificationVisible: false });
        }, NOTIFICATION_TIMEOUT);
      }
    }, NOTIFICATION_DEBOUNCE);

    updatePairs({
      [name]: value,
    });

    showSavedNotification();

    // this is to make sure we are validating updated redux value
    setTimeout(() => {
      if (Object.keys(errors).indexOf(name) > -1) {
        onValidate({ name });
      }
    }, 0);
  };

  const onBlur = (event: SyntheticInputEvent<HTMLInputElement>) => {
    const { name } = event.currentTarget;

    onValidate({ name });
  };

  useEffect(() => {
    _updateUITBCPairs({
      isBasicInfoValidated: false,
    });
  }, []);

  const onNext = (event) => {
    event.preventDefault();

    const _errors = onValidateAll({ checkTouch: false });

    const payload = flattenObject({
      contact: {
        title,
        titleKana,
        contactName,
        contactPhone,
        contactEmail,
      },
    });

    if (!Object.keys(_errors).find((key) => !!_errors[key])) {
      ReactGA.event({
        category: 'Basic Business | Next Step',
        action: 'Form Validation',
        label: 'Passed',
      });

      _updateUIPairs({ isLoading: true });
      axios
        .post(EMAIL_VERIFICATION_URL, {
          email: contactEmail,
        })
        .then((res) => {
          if (isEmailVerifyResultValid(res)) {
            ReactGA.event({
              category: 'Basic Business | Next Step',
              action: 'Email Verification',
              label: 'Passed',
            });

            _updateUIPairs({ isLoading: false });
            _updateUIPairs({
              isFromConfirm: false,
            });
            _updateUITBCPairs({
              isBasicInfoValidated: true,
            });

            if (isFromConfirm) {
              _updateUIPairs({ isFromConfirm: false });
              navigate('/confirm');
            } else {
              navigate('/business');
            }

            axios.post(SAVE_APPLICATION_URL, payload).catch(() => {});
          } else {
            _updateUIPairs({ isLoading: false });
            ReactGA.event({
              category: 'Basic Business | Next Step',
              action: 'Email Verification',
              label: 'Failed',
            });

            setEmailVerificationError('有効なメールアドレスを入力してください');
          }
        })
        .catch(() => {
          _updateUIPairs({ isLoading: false });
        });
    } else {
      _updateUIPairs({ isLoading: false });
      scrollToFirstError();

      ReactGA.event({
        category: 'Basic Business | Next Step',
        action: 'Form Validation',
        label: 'Failed',
      });
    }
  };

  return (
    <Layout indicatedMerchantType="business" page="basic-business">
      <div className="root">
        <main className="main">
          <div className="page-title">
            <h2>担当者情報 / 法人</h2>
            <p>入力内容は3時間自動保存されます。</p>
            <p>
              ※こちらはペイディとご契約の加盟店様用お問い合わせフォームです。
              <br />
              ペイディご利用者様は
              <OutboundLink
                className={'link-blue'}
                title="『お問い合わせフォーム（ペイディご利用者様）』"
                href={CUSTOMER_SERVICE_INQUIRY_FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                『お問い合わせフォーム（ペイディご利用者様）』
              </OutboundLink>
              からお問い合わせください <br /> <br />
              ※This is an inquiry form for merchants who would like to introduce Paidy payment
              service on its website. <br />
              If you are a Paidy customer, please contact us{' '}
              <OutboundLink
                className={'link-blue'}
                title="here"
                href={CUSTOMER_SERVICE_INQUIRY_FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                here
              </OutboundLink>
              .
            </p>
          </div>
          <form>
            <section className="form-box short">
              <div className="short-box">
                <fieldset>
                  <InputBox
                    name="title"
                    label="会社名"
                    placeholder="例〇〇株式会社"
                    errors={errors}
                    value={title}
                    onChange={onChange}
                    onBlur={onBlur}
                    size="full"
                    maxLength={100}
                  />
                </fieldset>
                <fieldset>
                  <InputBox
                    name="titleKana"
                    label="会社名（カナ）"
                    placeholder="例〇〇カブシキガイシャ"
                    errors={errors}
                    value={titleKana}
                    onChange={onChange}
                    onBlur={onBlur}
                    size="full"
                    maxLength={100}
                  />
                </fieldset>

                <fieldset>
                  <InputBox
                    name="contactName"
                    label="担当者名"
                    placeholder="例 山田 太郎"
                    errors={errors}
                    value={contactName}
                    onChange={onChange}
                    onBlur={onBlur}
                    maxLength={100}
                  />
                </fieldset>
                <fieldset>
                  <InputBox
                    name="contactPhone"
                    label="電話番号"
                    placeholder="例 0311110000(半角数字)"
                    errors={errors}
                    value={contactPhone}
                    onChange={onChange}
                    type={isAndroid ? 'number' : 'tel'}
                    onBlur={onBlur}
                  />
                </fieldset>
                <fieldset>
                  <InputBox
                    name="contactEmail"
                    label="メールアドレス"
                    placeholder="例 hello@paidy.com"
                    errors={errors}
                    value={contactEmail}
                    onChange={(event: SyntheticInputEvent<HTMLInputElement>) => {
                      setEmailVerificationError('');
                      onChange(event);
                    }}
                    onBlur={(event: SyntheticInputEvent<HTMLInputElement>) => {
                      setEmailVerificationError('');
                      onBlur(event);
                    }}
                    customError={emailVerificationError}
                  />

                  <p className="bottom">
                    当社の
                    <OutboundLink
                      className={'link-blue'}
                      title="個人情報保護に関する基本方針（プライバシーポリシー）"
                      href={TERMS_PERSONAL_INFO_CONSENT_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      個人情報保護に関する基本方針（プライバシーポリシー）
                    </OutboundLink>
                    にご同意の上、次へを押してください。
                  </p>
                </fieldset>
              </div>
            </section>
          </form>
        </main>
      </div>
      <footer className="form-footer">
        <button onClick={onNext}>{isFromConfirm ? '確認画面へ' : '次へ'}</button>
        {!isFromConfirm && (
          <Link className={cx('btn', 'secondary')} to="/">
            戻る
          </Link>
        )}
      </footer>
    </Layout>
  );
};

const mapStateToProps = (state) => ({
  ...state.basicBusiness,
  isNotificationVisible: state.ui.isNotificationVisible,
  isFromConfirm: state.ui.isFromConfirm,
});

const mapDispatchToProps = (dispatch: Function) => ({
  updatePairs: (pairs) => dispatch(updateFormPairs(pairs)),
  _updateUIPairs: (pairs) => dispatch(updateUIPairs(pairs)),
  _updateUITBCPairs: (pairs) => dispatch(updateUITBCPairs(pairs)),
  _updateChannel: (payload) => dispatch(updateChannel(payload)),
  _updateRefCode: (payload) => dispatch(updateRefCode(payload)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withValidation(formValidators)(BasicBusinessPage));
